import type { ZoneId } from './ids';

/**
 * Real astronomy turned into mechanics (GDD §3).
 * Distances are in astronomical units (AU), luminosity in solar units.
 */

/** Conservative habitable zone for a star of luminosity L (scales with √L). */
export function habitableZoneAu(luminositySun: number): { inner: number; outer: number } {
  const s = Math.sqrt(luminositySun);
  return { inner: 0.95 * s, outer: 1.67 * s };
}

/** Snow (frost) line: beyond it volatiles freeze and giants form (~2.7 AU for the Sun). */
export function frostLineAu(luminositySun: number): number {
  return 2.7 * Math.sqrt(luminositySun);
}

/**
 * Orbit semi-major axes. Real systems space orbits roughly geometrically,
 * so orbit i sits at 0.39 · 1.75^i AU (Mercury-like start).
 */
export function orbitAu(index: number): number {
  return 0.39 * 1.75 ** index;
}

/** Which zone an orbit at `au` belongs to for a star of luminosity L. */
export function zoneAt(au: number, luminositySun: number): ZoneId {
  const hz = habitableZoneAu(luminositySun);
  const frost = frostLineAu(luminositySun);
  if (au < hz.inner) return 'hot';
  if (au <= hz.outer) return 'habitable';
  if (au <= frost * 1.2) return 'belt';
  if (au <= frost * 4) return 'giants';
  return 'outer';
}

/** Stellar flux relative to Earth: inverse-square law. Drives solar collectors. */
export function solarFlux(au: number, luminositySun: number): number {
  return luminositySun / (au * au);
}

/**
 * Approximate RGB (0–1) of a black body at `kelvin`, after Tanner Helland's
 * fit of the CIE data. Good enough for star colours from 1 000 K to 40 000 K.
 */
export function blackbodyRgb(kelvin: number): [number, number, number] {
  const t = Math.min(Math.max(kelvin, 1000), 40000) / 100;
  const clamp = (v: number) => Math.min(Math.max(v, 0), 255) / 255;
  const r = t <= 66 ? 255 : 329.698727446 * (t - 60) ** -0.1332047592;
  const g =
    t <= 66
      ? 99.4708025861 * Math.log(t) - 161.1195681661
      : 288.1221695283 * (t - 60) ** -0.0755148492;
  const b = t >= 66 ? 255 : t <= 19 ? 0 : 138.5177312231 * Math.log(t - 10) - 305.0447927307;
  return [clamp(r), clamp(g), clamp(b)];
}
